import { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router'
import { useQueryParam } from 'use-query-params'
import { Grid, Typography, makeStyles, Container, Button } from '@material-ui/core'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import { useSearchClosedByTerm, ClosedByTerm, ClosedByTermKeyword } from '../services/um/useSearchClosedByTerm'
import { convertDateToFormatTH, convertDateToFormatEN } from '../utilities/date'
import useTranslate from '../i18n/useTranslate'
import CloseDuePanel from '../component/report/closed-due/Panel'
import closedDueExport from '../component/report/closed-due/report'
import useAuthenStore from '../stores/authen/authenStore'
import SearchAccordion from '../control/accordion/SearchAccordion'
import Table from '../component/report/closed-due/Table'
import useLoading from '../control/loading/useLoading'
import Dialog from '../control/dialog/dialog'

const ClosedDueForm = () => {
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const classes = useStyles()
    const history = useHistory()
    const authenStore = useAuthenStore()
    const loadingHook = useLoading()
    const { searchClosedByTerm, deleteProjectClose } = useSearchClosedByTerm()

    const dialogDeleteRef = useRef<Record<string, Function>>()
    const [dataList, setDataList] = useState<ClosedByTerm[]>([])
    const [keyword, setKeyword] = useQueryParam<string | undefined>('keyword')
    const [termYear, setTermYear] = useQueryParam<number | undefined | null>('termYear')
    const [selectedIDs, setSelectedIDs] = useState<number[]>([])

    const setSearchParam = (value: ClosedByTermKeyword) => {
        setKeyword(value.keyword)
        setTermYear(value.termYear)
    }

    const [initialValue, setInitialValue] = useState<ClosedByTermKeyword>({
        keyword: keyword,
        termYear: termYear ?? 3,
        userID: authenStore.profile.userId ?? 0,
    })

    useEffect(() => {
        if (authenStore.profile.userId) {
            onSubmit(initialValue)
        }
    }, [authenStore.profile.userId])

    const onSubmit = async (value: ClosedByTermKeyword) => {
        setInitialValue(value)
        const data = await searchClosedByTerm({
            keyword: value.keyword,
            termYear: value.termYear,
            userID: authenStore.profile.userId ?? 0,
        })
        setDataList(data)
        setSearchParam(value)
    }

    const onClear = async (form: any) => {
        form.reset()
        setInitialValue({
            userID: authenStore.profile.userId ?? 0,
        })
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    const onBack = () => {
        history.push('/dashboard')
    }

    const handleExportExcel = () => {
        if (!dataList.length) return
        closedDueExport({
            header: {
                termYear: termYear ?? 0,
                keyword: keyword ?? '-',
            },
            content: dataList.map((item) => ({
                projectID: item.projectNumber,
                receiveDate: convertDate(item.receiveDate),
                endDate: convertDate(item.closedDate),
                researcherName: item.name?.[lang] ?? '-',
                position: item.researcherTypeName?.[lang] ?? '-',
                projectNameTH: item.projectName?.['th'] ?? '-',
                projectNameEN: item.projectName?.['en'] ?? '-',
                projectType: item.projectType?.name?.[lang] ?? '-',
            })),
            translate: (value: string) => translate.get(value),
        })

        setSelectedIDs([])
    }

    const bindDeleteRef = (func: Record<string, Function>) => {
        dialogDeleteRef.current = { ...func }
    }

    const openConfirmDelete = () => {
        dialogDeleteRef.current?.openDialog()
    }

    const handleDelete = async () => {
        try {
            loadingHook.loadingOpen()
            await deleteProjectClose({ projectIDs: selectedIDs ?? [] })
            setSelectedIDs([])
            dialogDeleteRef.current?.closeDialog()
            await onSubmit(initialValue)
        } finally {
            loadingHook.loadingClose()
        }
    }

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <SearchAccordion title={translate.get('UM.CLOSED_DUE.HEADER')} isBack={true} onClickBack={onBack}>
                    <CloseDuePanel onSubmit={onSubmit} initialValue={initialValue} onClear={onClear} />
                </SearchAccordion>
                <Grid container justify="space-between" alignItems="flex-end" className={classes.container}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" className={classes.header}>
                            {translate.get('UM.CLOSED_DUE.SEARCH_RESULTS')}
                        </Typography>
                        <Typography variant="subtitle1">
                            {translate.get('UM.CLOSED_DUE.PROJECT_TOTAL', [`${dataList.length}`])}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="contained"
                                    color="secondary"
                                    disabled={!selectedIDs.length}
                                    onClick={() => openConfirmDelete()}
                                >
                                    <Typography variant="button">
                                        {translate.get('UM.CLOSED_DUE.DELETE_BUTTON')}
                                    </Typography>
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={
                                        <GetAppRoundedIcon style={{ color: dataList.length ? '#DB5F8E' : '#e0e0e0' }} />
                                    }
                                    disabled={!dataList.length}
                                    onClick={handleExportExcel}
                                >
                                    <Typography variant="button">{translate.get('BUTTON.EXPORT')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Table
                            dataList={dataList}
                            selectedIds={selectedIDs}
                            onSelectionChange={(ids: number[]) => setSelectedIDs(ids)}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Dialog
                onRef={bindDeleteRef}
                type="alert-confirm"
                title={translate.get('ALERT.TITLE.WARNING')}
                content={translate.get('ALERT.CONTENT.NEED_TO_DELETE')}
                onClickConfirm={handleDelete}
            />
        </Container>
    )
}

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    container: {
        margin: '15px 0px',
        width: '100%',
    },
    header: {
        marginBottom: '16px',
        fontWeight: 'bold',
    },
}))

export default ClosedDueForm
function deleteProjectClose() {
    throw new Error('Function not implemented.')
}
